// How the review reads a walk answer: the field CONFIG says what a value
// IS — a date, a choice, a name — so formatting keys off the field's
// type and its own choice labels, never off per-ref special cases. The
// same helpers dress the review's curated rows, so a date reads
// "Feb 15, 2025" wherever it appears.

import { substituteVariables } from "components/Form/variables";
import { evaluateCondition } from "components/FormV2/conditions";
import type {
  V2ResolvedField,
  V2ResolvedScreen,
} from "components/FormV2/types";

export interface AnswerRow {
  label: string;
  value: string;
}

const MONTH_DAY_YEAR = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

export function text(value: unknown): string {
  if (typeof value === "number") return String(value);
  return typeof value === "string" ? value.trim() : "";
}

export function nameOf(value: unknown): string {
  if (typeof value !== "object" || value === null) return "";
  const parts = value as Record<string, unknown>;
  return [parts.first_name, parts.middle_name, parts.last_name, parts.suffix]
    .map(text)
    .filter(Boolean)
    .join(" ");
}

export function addressOf(value: unknown): string {
  if (typeof value !== "object" || value === null) return "";
  const parts = value as Record<string, unknown>;
  const street = [text(parts.address_line_1), text(parts.address_line_2)]
    .filter(Boolean)
    .join(", ");
  const where = [
    text(parts.city),
    [text(parts.state), text(parts.zip)].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
  return [street, where].filter(Boolean).join(" · ");
}

/** "02/15/2025" (the masked field's shape) or an ISO date, read aloud as
 * "Feb 15, 2025". Anything unreadable shows as typed — a strange value
 * is still the family's answer. */
export function dateLabel(value: unknown): string {
  const raw = text(value);
  const masked = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(raw);
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  const ymd = masked
    ? { year: masked[3], month: masked[1], day: masked[2] }
    : iso
      ? { year: iso[1], month: iso[2], day: iso[3] }
      : null;
  if (!ymd) return raw;
  const date = new Date(
    Number(ymd.year),
    Number(ymd.month) - 1,
    Number(ymd.day),
  );
  return Number.isNaN(date.getTime()) ? raw : MONTH_DAY_YEAR.format(date);
}

/** Ten stored digits dressed "(480) 200-0000"; anything else as typed. */
export function phoneLabel(value: unknown): string {
  const raw = text(value);
  const digits = raw.replace(/\D/g, "");
  if (digits.length !== 10) return raw;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/** A bare ref ("power_of_attorney") dressed for reading, when a choice
 * value arrives without a configured label to speak for it. */
export function dressedRef(value: unknown): string {
  const raw = text(value);
  if (!raw) return "";
  return (raw.charAt(0).toUpperCase() + raw.slice(1)).replace(/_/g, " ");
}

function choiceLabels(field: V2ResolvedField, value: unknown): string {
  const choices = field.variant.field_properties.choices ?? [];
  const chosen = Array.isArray(value) ? value : [value];
  return chosen
    .map(
      (ref) =>
        choices.find((choice) => choice.ref === ref)?.label ?? dressedRef(ref),
    )
    .filter(Boolean)
    .join(", ");
}

export function fieldAnswerLabel(
  field: V2ResolvedField,
  value: unknown,
): string {
  switch (field.type) {
    case "yes_no":
      return value === true ? "Yes" : value === false ? "No" : "";
    case "date":
      return dateLabel(value);
    case "phone_number":
      return phoneLabel(value);
    case "dropdown":
    case "choices":
      return choiceLabels(field, value);
    case "full_name":
      return nameOf(value);
    case "location":
      return addressOf(value);
    default:
      return text(value);
  }
}

/** A run of rows under an optional sub-head — how a screen's
 * SectionHeading fields carve its answers on the review. A block whose
 * rows all dropped keeps its heading out too. */
export interface AnswerBlock {
  heading: string | null;
  rows: AnswerRow[];
}

/** One review group: consecutive walk screens sharing a section, their
 * answers in walk order. The Change link lands on the first screen. */
export interface ReviewSectionGroup {
  label: string;
  screenRefs: string[];
  blocks: AnswerBlock[];
}

/**
 * One walk screen's questions and answers as blocks, in walk order:
 * every visible answer-bearing field with a present value, labeled by
 * the variant the family actually saw, formatted by its type; a
 * SectionHeading field opens a new block and becomes its sub-head.
 * Hidden fields stay out even when an orphaned answer lingers under
 * them — what the review shows is what the walk asked. Unanswered
 * fields are omitted, and a block with no surviving rows drops whole.
 */
export function screenAnswerBlocks(
  screen: V2ResolvedScreen,
  values: Record<string, unknown>,
): AnswerBlock[] {
  const interpolate = (input: string) =>
    // Titles carry {{field:…}} templates ("Has
    // {{field:insured_name.first_name}} used tobacco…") — resolve them
    // over the walk's answers, exactly as the walk itself displayed
    // them. Their fallbacks cover anything unanswered.
    substituteVariables(input, { answers: values });
  const visible = screen.fields.filter(
    (field) =>
      field.variant.visible_when === null ||
      evaluateCondition(field.variant.visible_when, {
        answers: values,
        context: {},
        variables: {},
      }),
  );
  const blocks: AnswerBlock[] = [];
  let open: AnswerBlock = { heading: null, rows: [] };
  const close = () => {
    if (open.rows.length > 0) blocks.push(open);
  };
  for (const field of visible) {
    if (field.type === "section_heading") {
      close();
      open = { heading: interpolate(field.variant.title), rows: [] };
      continue;
    }
    const value = fieldAnswerLabel(field, values[field.ref]);
    if (value === "") continue;
    open.rows.push({ label: interpolate(field.variant.title), value });
  }
  close();
  return blocks;
}

/**
 * The review's groups from the walk itself: consecutive body screens
 * sharing a `section` collapse under one label, in walk order — a new
 * screen or question lands on the review with no curation. Groups whose
 * screens produced no rows drop whole.
 */
export function sectionGroups(
  screens: readonly V2ResolvedScreen[],
  values: Record<string, unknown>,
): ReviewSectionGroup[] {
  const groups: ReviewSectionGroup[] = [];
  for (const screen of screens) {
    const raw = screen.variant.properties.section;
    const label = raw ? substituteVariables(raw, { answers: values }) : "";
    const blocks = screenAnswerBlocks(screen, values);
    const last = groups[groups.length - 1];
    if (last && last.label === label) {
      last.screenRefs.push(screen.ref);
      last.blocks.push(...blocks);
      continue;
    }
    groups.push({ label, screenRefs: [screen.ref], blocks });
  }
  return groups.filter((group) =>
    group.blocks.some((block) => block.rows.length > 0),
  );
}
